// The team's jobs as pages (G4: `JobPage {items, next}` with an opaque cursor), built from the
// generated `listJobs` and its key, as the library's list is. The stream invalidates every list
// of the team by the key's path (event-handlers.ts), so a status change reaches these pages.
import { useInfiniteQuery, type InfiniteData, type QueryClient } from "@tanstack/react-query";

import { getListJobsQueryKey, listJobs } from "../../api/generated/endpoints/generation/generation";
import type { GenerationType, Job, JobListStatus, JobPage, Medium } from "../../api/generated/model";
import type { StreamStatus } from "../../api/events";
import { soonest, soonestExpiry, urlRefreshIn } from "../../api/signed-urls";
import { FALLBACK_REFETCH_MS, canChange } from "./refetch";

export interface JobFilter {
  status: JobListStatus;
  brandId?: string | undefined;
  /** These job types alone (the AI Actors', the Templates'); without them the list leaves the AI
   * Actors' `image.actor` out (#387). */
  types?: readonly GenerationType[] | undefined;
  /** One medium's jobs alone: a Studio page's My Generations (Image, Video). */
  medium?: Medium | undefined;
  /** Jobs a page: 50 unless the list says (`GENERATIONS_PAGE`). */
  pageSize?: number | undefined;
}

const PAGE_SIZE = 50;
/**
 * Live's page of a My Generations list (`DEFAULT_GENERATIONS_PAGE_LIMIT`), for a list the server
 * filters to its own jobs: by their types (the AI Actors', the Templates', the Sounds') or their
 * medium (the Studio's Image and Video pages; `medium`, issue 784).
 */
export const GENERATIONS_PAGE = 5;
/** The key's last part, keeping these pages apart from a plain `useListJobs` of the same params. */
const PAGES = "pages";

function anyCanChange(pages: readonly JobPage[] | undefined): boolean {
  return (pages ?? []).some((page) => page.items.some((job) => canChange(job.status)));
}

/** When the pages' thumbnails need fresh URLs (G5): before the first page's URLs expire. */
function urlsRefreshIn(pages: readonly JobPage[] | undefined): number | false {
  const items = (pages ?? []).flatMap((page) => page.items);
  return urlRefreshIn(soonestExpiry(items.map((job) => job.urls_expire_at)));
}

/**
 * The jobs of the filter, newest first. While a listed job can still change and the stream is
 * not live, the pages are read again every 15 s, and on window focus, as the job page does. The
 * pages are also read again before their thumbnails' signed URLs expire.
 */
export function useJobList(teamId: string, filter: JobFilter, stream: StreamStatus | undefined) {
  const params = {
    status: filter.status,
    ...(filter.brandId === undefined ? {} : { brand_id: filter.brandId }),
    ...(filter.types === undefined ? {} : { type: [...filter.types] }),
    ...(filter.medium === undefined ? {} : { medium: filter.medium }),
  };
  const size = filter.pageSize ?? PAGE_SIZE;
  return useInfiniteQuery({
    // The page size is in the key: a list of 5 a page never meets one of 50.
    queryKey: [...getListJobsQueryKey(teamId, { ...params, limit: size }), PAGES],
    queryFn: ({ pageParam, signal }) =>
      listJobs(
        teamId,
        { ...params, limit: size, ...(pageParam === undefined ? {} : { after: pageParam }) },
        { signal },
      ),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next ?? undefined,
    refetchInterval: (query) => {
      const pages = query.state.data?.pages;
      const fallback = stream !== "live" && anyCanChange(pages) ? FALLBACK_REFETCH_MS : false;
      return soonest(fallback, urlsRefreshIn(pages));
    },
    refetchOnWindowFocus: (query) => (anyCanChange(query.state.data?.pages) ? "always" : true),
  });
}

/**
 * Every listed page of the team's jobs with each job through `change`, for a write whose answer
 * says what the next read will (a portrait saved as an actor, an actor deleted), so the pages show
 * it at once rather than after a read.
 */
export function patchListedJobs(queryClient: QueryClient, teamId: string, change: (job: Job) => Job) {
  queryClient.setQueriesData<InfiniteData<JobPage>>(
    { queryKey: getListJobsQueryKey(teamId), predicate: (query) => query.queryKey.at(-1) === PAGES },
    (data) =>
      data === undefined
        ? data
        : { ...data, pages: data.pages.map((page) => ({ ...page, items: page.items.map(change) })) },
  );
}
